import { Box, Link, Stack } from 'shared';
import { LetterIcon } from 'App/components';
import classNames from 'classnames';
import React from 'react';
import { SiteStateIcon } from 'Sites/components/SiteStatusIcon';
import { ThemedHeader } from 'Sites/components/ThemedHeader';
import { isStyledTheme, getThemeConfig } from 'Sites/themes';
import { SentryApiClient } from '_generated/api';
import styles from './CompanySiteDisplay.module.css';
import { ContextThemedHeaderWithOptions } from 'Sites/components/ThemedHeader/ThemedHeader';

export interface CompanySiteDisplayProps {
  site: SentryApiClient.SiteDTO;
}

/**
 * Themed header wrapper for site display.
 * Uses ThemedHeader for styled themes, fallback layout for Default theme.
 */
const ThemedSiteHeader: React.FC<{ site: SentryApiClient.SiteDTO }> = ({
  site,
}) => {
  const theme = site.uiTheme;
  const isStyled = isStyledTheme(theme);

  // Use ThemedHeader for styled themes
  if (isStyled && theme != null) {
    return (
      <ThemedHeader
        theme={theme}
        title={site.name}
        subtitle={site.tagLine}
        variant="pill-separate"
      />
    );
  }

  // Default theme fallback - use LetterIcon with basic styling
  return (
    <div className={styles.themedHeader}>
      <div className={styles.themedIconWrapper}>
        <LetterIcon name={site.name} />
      </div>
      <div className={styles.themedHeaderText}>
        <h3>{site.name}</h3>
        {site.tagLine && <div className={styles.tagline}>{site.tagLine}</div>}
      </div>
    </div>
  );
};

export const CompanySiteDisplay: React.FC<CompanySiteDisplayProps> = ({
  site,
}) => {
  const themeConfig = getThemeConfig(site.uiTheme);
  const borderColor = '#f0f0f0';

  return (
    <Link
      to={`/dashboard/site/${site.id}`}
      className={styles.wrapper}
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: '8px',
      }}
    >
      <Stack alignment="middle" gap="apart">
        <ThemedSiteHeader site={site} />
        <SiteStateIcon state={site.stateType} />
      </Stack>
      <Box padding={{ top: 'xl' }}>{site.details}</Box>
      <div className={classNames(styles.strapLine, styles.subtle)}>
        {site.strapLine}
      </div>
    </Link>
  );
};
